import {useCallback, useState} from "react";
import {useDropzone} from "react-dropzone";
import {Alert, Badge, Button, Card, Modal, ProgressBar, Table} from "react-bootstrap";
import ReactJson from "react18-json-view";
import 'react18-json-view/src/style.css'
import PropTypes from "prop-types";
import {v4 as uuidv4} from "uuid";
import {HeaderTitle} from "../HeaderTitle";
import {handleDownloadFile} from "../../utils/handlers";
import ButtonWithIcon from "../ButtonWithIcon";
import classNames from "classnames";
import {toSizeFileSizeUnit} from "../../utils/helpers/toSizeFileUnit";
import {ChevronDown, ChevronUp} from "react-bootstrap-icons";
import {Icons} from "../Icons";
import {useUploadedFiles} from "../../../api/domain/files/queries/useUploadedFiles.jsx";
import {useConfigFiles} from "../../../api/domain/files/queries/useConfigFiles.jsx";
import {useUploadFile} from "../../../api/domain/files/mutations/useUploadFile.jsx";
import {useUsunFile} from "../../../api/domain/files/mutations/useUsunFile.jsx";
import {useUpdateFile} from "../../../api/domain/files/mutations/useUpdateFile.jsx";


const Uploader = (props) => {

        const [selectedFiles, setSelectedFiles] = useState({});
        const [showConfirm, setShowConfirm] = useState(false);
        const [toRemove, setToRemove] = useState(null);
        const [showEdit, setShowEdit] = useState(false);
        const [toEdit, setToEdit] = useState(null);
        const [info, setInfo] = useState(null);

        const uploaded = useUploadedFiles(props.uploadedUrl)
        const uploadFile = useUploadFile(props.uploadUrl)
        const usun = useUsunFile(props.deleteUrl)
        const update = useUpdateFile(props.editUrl)
        const config = useConfigFiles(props.configUrl, props.config)

        function reorderArray(currentIndex, newIndex, setter, list) {
            if (currentIndex < 0 || currentIndex > uploaded.data?.list.length ||
                newIndex < 0 || newIndex > uploaded.data?.list.length ||
                currentIndex === newIndex) {
                return [...list]
            }
            const elementToMove = list[currentIndex];
            const newArray = [...list];
            newArray.splice(currentIndex, 1);
            newArray.splice(newIndex, 0, elementToMove);
            const sortChangedArray = [currentIndex, newIndex]
            sortChangedArray.forEach((element) => {
                update.mutateAsync({data: {...newArray[element], sort: element}}).then(() => {
                        setter(newArray)
                    }
                )
            })

        }

        const onDrop = useCallback(
            (acceptedFiles,
             // _fileRejections
            ) => {
                setInfo(null);

                const validate = (f) => {
                    const size =
                        f.size <= 1024 * config["file-max-size"] || !config["file-max-size"] ? null : "Plik jest za duzy";
                    const type =
                        config["file-types"]?.includes(f.name.split(".").pop()) || !config["file-types"]
                            ? null
                            : "Nieakceptowany typ pliku";
                    return [size, type]?.filter((e) => e);
                };

                let sfl = Object.keys(selectedFiles)?.filter((k) => selectedFiles[k] !== undefined).length;
                const files = acceptedFiles.reduce((a, f) => {
                    if (config["files-limit"] && sfl >= config["files-limit"]) {
                        setInfo(`Przekroczono limit jednorazowego uploadu: ${config["files-limit"]}`);
                        return a;
                    }
                    sfl++;
                    return {...a, [uuidv4()]: {...props.fileConst, file: f, progress: 0, error: validate(f)}};
                }, {});
                setSelectedFiles((sf) => ({...sf, ...files}));
            },
            // eslint-disable-next-line
            [config, selectedFiles]
        );

        const {getRootProps, getInputProps, isDragActive, open} = useDropzone({onDrop, noClick: true});

        const setAttr = (uuid) => {
            return (key, value) => setSelectedFiles((sf) => ({...sf, [uuid]: {...sf[uuid], error: [], [key]: value}}));
        };

        const uploadAll = async () => {
            let cu = uploaded.data?.list?.length;
            for (const uuid in selectedFiles) {
                if (selectedFiles[uuid] && selectedFiles[uuid].error.length === 0) {
                    if (cu < (+config?.data?.["files-total-limit"] || 50)) {
                        const r = await upload(uuid, true);
                        cu = r ? cu + 1 : cu;
                    } else {
                        setSelectedFiles((sf) => ({
                            ...sf,
                            [uuid]: {...sf[uuid], error: ["Przekroczono limit ilości plików"]},
                        }));
                    }
                }
            }
        };

        const upload = async (uuid, multi) => {
            if (!multi && uploaded?.data?.list?.length >= config?.data?.["files-total-limit"]) {
                setSelectedFiles((sf) => ({...sf, [uuid]: {...sf[uuid], error: ["Przekroczono limit ilości plików"]}}));
                return;
            }

            const formData = new FormData();

            for (const key in selectedFiles[uuid]) {
                formData.append(key, selectedFiles[uuid][key]);
            }

            const maxSort = uploaded?.data?.list?.reduce((a, b) => Math.max(a, b.sort), 0)

            const index = Object.keys(selectedFiles)?.filter((k) => selectedFiles[k] !== undefined).indexOf(uuid) || 0

            formData.append('sort', (maxSort + 1 + index) || 0)

            const uploadConfig = {
                onUploadProgress: (e) => {
                    const percentCompleted = Math.round((e.loaded * 100) / e.total);
                    setSelectedFiles((sf) => ({...sf, [uuid]: {...sf[uuid], progress: percentCompleted}}));
                },
            };

            await uploadFile.mutateAsync({data: formData, config: uploadConfig}).then((r) => {
                if (r.status === 'success') {
                    setInfo(null);
                    setSelectedFiles((sf) => ({...sf, [uuid]: undefined}));
                } else {
                    setSelectedFiles((sf) => ({
                        ...sf,
                        [uuid]: {
                            ...sf[uuid],
                            progress: 0,
                            error: ["Błąd danych"],
                        },
                    }));
                }
            });
        };

        const removeSelected = (uuid) => {
            setInfo(null);
            setSelectedFiles((sf) => ({...sf, [uuid]: undefined}));
        };

        const removeUploaded = (id) => {
            usun.mutateAsync({id}).then(() => {
                setToRemove(null);
                setShowConfirm(false);
            });
        };

        const confirmRemove = (f) => {
            setToRemove(f);
            setShowConfirm(true);
        };

        const edit = (f) => {
            setToEdit({...f});
            setShowEdit(true);
        };

        const setEditedAttr = (key, value) => {
            setToEdit((te) => ({...te, [key]: value}));
        };

        const save = () => {
            update.mutateAsync({data: {...toEdit, ...props.fileConst}}).then(() => {
                setToEdit(null);
                setShowEdit(false);
            });
        };

        const renderDebug = () => {
            return props.debug ? (
                <Card className="mb-2 mt-2 ">
                    <Card.Body>
                        <ReactJson
                            src={{
                                selectedFiles,
                                uploaded: uploaded.data?.list,
                                config,
                                showConfirm,
                                toRemove,
                                showEdit,
                                toEdit,
                                info
                            }}
                            style={{fontSize: 10}}
                            name="values"
                            collapsed={true}
                        />
                    </Card.Body>
                </Card>
            ) : (
                <></>
            );
        };

        const renderDropZone = () => (
            <div
                {...getRootProps()}
                style={{
                    width: "100%",
                    height: 180,
                    border: "5px dashed #0d47a1",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    color: "#0d47a1",
                    backgroundColor: "rgba(13, 71, 161, 0.3)",
                }}
            >
                <input aria-label={'dropzone'} {...getInputProps()} />
                {isDragActive ? (
                    <p>Upuść pliki tutaj ...</p>
                ) : (
                    <div className={"d-flex flex-column"}>
                        <div>Kliknij przycisk lub przeciągnij na niebieskie pole, aby dodać pliki</div>
                        <Button className="align-self-center mt-1 flex-fill" size="sm" type="button"
                                onClick={() => open()}>
                            Wybierz pliki
                        </Button>
                    </div>
                )}
            </div>
        );

        const renderSelected = () => (
            <>
                <Card className={"mt-3"}>
                    <div className={"p-3 pb-0"}>
                        <HeaderTitle noHr nav={false} size={"h3"} title={"Wybrane pliki"}/>
                    </div>
                    <Card.Body>
                        <Table>
                            <thead>
                            <tr>
                                <th>Nazwa</th>
                                <th>Rozmiar</th>
                                {props.headers}
                                <th>Postęp</th>
                                <th>Operacje</th>
                            </tr>
                            </thead>
                            <tbody>
                            {info && (
                                <tr>
                                    <td colSpan={42}>
                                        <Alert variant="danger">{info}</Alert>
                                    </td>
                                </tr>
                            )}

                            {Object.keys(selectedFiles).map((uuid, i) =>
                                selectedFiles[uuid] ? (
                                    <tr key={uuid}>
                                        <td>{selectedFiles[uuid].file.name}</td>
                                        <td>{toSizeFileSizeUnit(selectedFiles[uuid].file.size, true, 1)}</td>
                                        {props.rowSelected(selectedFiles[uuid], setAttr(uuid))}
                                        <td>
                                            <ProgressBar
                                                title={"postęp"}
                                                className={"mt-1"}
                                                now={selectedFiles[uuid].progress}
                                                label={`${selectedFiles[uuid].progress}%`}
                                            />

                                            {selectedFiles[uuid]?.error?.map((e, i) => (
                                                <Badge key={i} bg="danger" style={{marginRight: 2}}>
                                                    {e}
                                                </Badge>
                                            ))}
                                        </td>
                                        <td>
                                            <div className={"d-flex gap-1"}>
                                                {selectedFiles[uuid].error.length === 0 && (
                                                    <ButtonWithIcon
                                                        size={"sm"}
                                                        title={"przekaż plik"}
                                                        onClick={() => upload(uuid, false)}
                                                        icon={<Icons.upload/>}
                                                    />
                                                )}

                                                <ButtonWithIcon
                                                    size={"sm"}
                                                    title={"rezygnuj"}
                                                    variant={"warning"}
                                                    onClick={() => removeSelected(uuid)}
                                                    icon={<Icons.close/>}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr key={i}>
                                    </tr>
                                )
                            )}
                            </tbody>
                        </Table>

                        <ButtonWithIcon
                            text={"Przekaż wszystkie"}
                            variant={"success"}
                            onClick={() => uploadAll()}
                            icon={<Icons.upload/>}
                        />
                    </Card.Body>
                </Card>
            </>
        );


        const renderUploaded = () => {

                return <Card className={'mt-3'}>
                    <Card.Body>

                        <HeaderTitle nav={false} noHr size={"h4"} title={"Pliki w repozytorium"}/>

                        <Table striped>
                            <thead>
                            <tr>
                                <th>Nazwa</th>
                                <th>Rozmiar</th>
                                {props.headers}
                                <th>Operacje</th>
                            </tr>
                            </thead>
                            <tbody>
                            {uploaded?.data?.list?.map((f, i) => {

                                    const rowClasses = classNames({"text-muted": f.aktualny === 'Nie',});

                                    return <tr key={i} className={rowClasses}>
                                        <td><span>[{i + 1}] {f.oryg_name || f.nazwa}</span></td>
                                        <td><small>{toSizeFileSizeUnit(f.size, true, 1)}</small></td>
                                        {props.rowUploaded(f)}
                                        <td>
                                            <div className={"d-flex gap-1"}>
                                                <ButtonWithIcon
                                                    // disabled={props.mode !== 'edit' || props.disableEdit || props.disable || f?.wersja_kom !== props.fileConst?.wersja_kom}
                                                    size={"sm"}
                                                    title={"edytuj plik"}
                                                    onClick={() => edit(f)}
                                                    icon={<Icons.edit/>}
                                                />

                                                <ButtonWithIcon
                                                    size={"sm"}
                                                    title={"pobierz plik"}
                                                    onClick={() => handleDownloadFile(f.file_id, props.downloadUrl)}
                                                    icon={<Icons.download/>}
                                                />
                                                {!props.preventDelete && (
                                                    <ButtonWithIcon
                                                        size={"sm"}
                                                        // disabled={props.mode !== 'edit' || props.disableDelete || props.disable || f?.wersja_kom !== props.fileConst?.wersja_kom}
                                                        variant={"danger"}
                                                        title={"usuń plik"}
                                                        onClick={() => confirmRemove(f)}
                                                        icon={<Icons.delete/>}
                                                    />
                                                )}

                                                {props.reordering && <>
                                                    <ButtonWithIcon
                                                        disabled={props.mode !== 'edit' || i === 0}
                                                        variant={'secondary'}
                                                        size={"sm"}
                                                        title={"przesuń plik w górę"}
                                                        onClick={() => reorderArray(i, i - 1)}

                                                        icon={<ChevronUp/>}
                                                    />
                                                    <ButtonWithIcon
                                                        variant={'secondary'}
                                                        disabled={props.mode !== 'edit' || i === uploaded?.data?.list?.length - 1}
                                                        size={"sm"}
                                                        title={"przesuń plik w dół"}
                                                        onClick={() => reorderArray(i, i + 1, uploaded.data?.list, uploaded.data?.list)}
                                                        icon={<ChevronDown/>}
                                                    />

                                                </>}
                                            </div>
                                        </td>
                                    </tr>
                                }
                            )}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            }
        ;

        const renderEditModal = () => {
            return (
                <Modal size={"xl"} show={showEdit} onHide={() => setShowEdit(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Edycja załącznika {toEdit?.id}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>{toEdit && props.editForm(toEdit, setEditedAttr)}</Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEdit(false)}>
                            Rezygnuję
                        </Button>
                        <Button variant="warning" onClick={() => save(toEdit)}>
                            MODYFIKUJ
                        </Button>
                    </Modal.Footer>
                </Modal>
            );
        };

        const renderConfirmModal = () => (
            <Modal show={showConfirm} onHide={() => setShowConfirm(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Usuwanie PLIKU</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <p>
                        {" "}
                        {props.modalBodyText} {toRemove?.oryg_name}
                    </p>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirm(false)}>
                        Rezygnuję
                    </Button>
                    <Button variant="danger" onClick={() => removeUploaded(toRemove?.file_id)}>
                        {props.modalDeleteButtonText}
                    </Button>
                </Modal.Footer>
            </Modal>
        );

        return (
            <>
                {props.debug && window.debug && renderDebug()}

                <div className={`d-flex flex-${props.flexDirectionRow ? 'row' : 'column'}`}>
                    {renderDropZone()}

                    {renderSelected()}
                </div>
                {!props.hideUploaded && renderUploaded()}

                {renderEditModal()}
                {renderConfirmModal()}
            </>
        );
    }
;


Uploader.propTypes = {
    uploadUrl: PropTypes.string, // POST url do uploadu
    uploadedUrl: PropTypes.string, // GET url zaladowane pliki
    editUrl: PropTypes.string, // url edycji pliku
    deleteUrl: PropTypes.string, // url usuwania pliku
    configUrl: PropTypes.string, // url konfiguracji dla plikow
    config: PropTypes.object, // jezeli nie url to obiekt
    fileConst: PropTypes.object, // stale informacje o pliku np nabor_id albo wniosek_id, cos czego uzytkownik nie edytuje na formularzu a musi byc wyslane razem z plikiem
    headers: PropTypes.node, // naglowki customowych kolumn tabelek wybrane i wyslane
    rowSelected: PropTypes.func, // customowe komorki tabeli wybranych plikow
    rowUploaded: PropTypes.func, // customowe komorki tabeli wyslanych plikow
    editForm: PropTypes.func, // formularz do edycji otwierany w modalu
    debug: PropTypes.bool,
    modalDeleteButtonText: PropTypes.string, // text butona akcji
    modalBodyText: PropTypes.string,
    preventDelete: PropTypes.bool,
    hideUploaded: PropTypes.bool,
    hideSelected: PropTypes.bool,
    flexDirectionRow: PropTypes.bool,
    downloadUrl: PropTypes.string,
    disable: PropTypes.bool,
    disableDelete: PropTypes.bool,
    disableEdit: PropTypes.bool,
    disableDownload: PropTypes.bool,
    reordering: PropTypes.bool,
    mode: PropTypes.oneOf(['edit', 'view']),
    refetch: PropTypes.func,

};

export default Uploader;
